import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useSEO } from "../utils/seo";
import { ProjectContinuity } from "../components/ProjectContinuity";

const industries = [
  { name: "Pipelines & Midstream", body: "ROW, easements, title and survey-access coordination for gathering and transmission corridors." },
  { name: "Transmission", body: "Corridor review, landowner negotiation and tract tracking for developers, EPCs and utilities." },
  { name: "Water & Utilities", body: "Land management, easements and GIS tracking for water infrastructure and utility service territories." },
  { name: "Renewable Energy", body: "Site control, leases and title support for wind, solar and hybrid projects." },
  { name: "Battery Storage", body: "Land control, gen-tie review and access coordination for BESS projects." },
  { name: "Access & Infrastructure", body: "Land and access planning for roads, ancillary corridors and tie-lines." },
  { name: "Energy Development", body: "Structured land work for upstream, midstream, LNG and industrial projects." },
  { name: "Land Development", body: "Ownership, title and access review for greenfield and industrial development." },
  { name: "Carbon Capture & CO2 Infrastructure", body: "Land, pore-space and ROW coordination for capture facilities, CO2 corridors and sequestration siting." },
];

const Industries = () => {
  useSEO({
    title: "Industries | Onward Land Solutions",
    description:
      "Land services for private power and data infrastructure, pipelines, transmission, renewables, storage, water and carbon capture. Connect site review with corridor planning.",
    canonicalPath: "/industries",
  });

  return (
    <>
      <section style={{ background: "linear-gradient(180deg, #090A0C 0%, #15171B 100%)" }} className="pt-24 pb-16 relative overflow-hidden">
        <div className="ols-parcel-grid absolute inset-0" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="ols-eyebrow ols-eyebrow--light mb-4">Industries</p>
          <h1 className="ols-h1 max-w-4xl">Land for power and infrastructure.</h1>
          <p className="text-lg text-slate-200 max-w-3xl mt-6">
            Compare the site. Understand the connections. Coordinate the land work.
          </p>
        </div>
      </section>

      <section className="ols-section" style={{ background: "#fff" }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-12" data-testid="industries-private-power">
          <p className="ols-eyebrow mb-3">Private Power &amp; Data Infrastructure</p>
          <h2 className="ols-h2" style={{ color: "var(--ols-navy)" }}>The site and the infrastructure around it.</h2>
          <p className="text-[15px] leading-relaxed max-w-3xl mt-5" style={{ color: "var(--ols-body)" }} data-testid="private-power-scope">Generation sites, gas laterals, water, fiber, access roads and future grid connections. AreaScore reviews the site; PathScore evaluates connecting corridors.</p>
          <p className="text-[14px] leading-relaxed max-w-3xl mt-4" style={{ color: "var(--ols-body)" }} data-testid="private-power-boundary">Land review does not establish power, capacity, interconnection or site readiness. Onward provides survey access and coordination, not licensed surveying.</p>
          <div className="flex flex-wrap gap-6 mt-5">
            <Link to="/area-score?source=industries_private_power" className="underline font-semibold text-sm" style={{ color: "var(--ols-navy)" }} data-testid="private-power-areascore-link">Explore AreaScore</Link>
            <Link to="/path-score?source=industries_private_power" className="underline font-semibold text-sm" style={{ color: "var(--ols-navy)" }} data-testid="private-power-pathscore-link">Explore PathScore</Link>
          </div>
        </div>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 gap-6">
          {industries.map((ind) => (
            <div key={ind.name} className="ols-card" data-testid={`industry-${ind.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
              <h2 className="text-[22px] font-bold mb-4" style={{ color: "var(--ols-navy)", fontFamily: "Playfair Display, Georgia, serif" }}>{ind.name}</h2>
              <p className="text-[15px] leading-relaxed" style={{ color: "var(--ols-body)" }}>{ind.body}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="ols-section" style={{ background: "#15171B", color: "#F5F2EC" }} data-testid="industries-continuity-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8"><ProjectContinuity id="industries" /></div>
      </section>

      <section style={{ background: "var(--ols-limestone)" }} className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="ols-h2 mb-8">Bring us the land question.</h2>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link to="/contact?source=industries_footer_cta" className="ols-btn-primary" data-testid="industries-project-cta">Talk to Onward <ArrowRight className="h-4 w-4" /></Link>
            <Link to="/path-score?source=industries_footer_cta" className="ols-btn-secondary ols-btn-secondary--dark" data-testid="industries-pathscore-cta">Explore PathScore</Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default Industries;
